import type { RefObject } from 'react';
import { useEffect } from 'react';

import type { AgGridReact } from 'ag-grid-react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { findCurrentUserEvent } from '../../../shared/lib';
import type { UserFunction } from '../../../shared/types';
import { currentEventIdState } from '../../../state/event';
import type { User } from '../../../types';
import { useNewEntityRow, isNewRowEntity } from '../state/newEntity';
import type { UseNewEntityRowParams } from '../state/newEntity/types';

export function useInlineAdding<T>(
  gridRef: RefObject<AgGridReact<unknown>>,
  newEntityParams: UseNewEntityRowParams<T>,
) {
  const currentEventId = useRecoilValue(currentEventIdState);
  const { newEntity } = useNewEntityRow(newEntityParams);
  const setNewEntity = useSetRecoilState(newEntityParams.newEntityAtom);
  const editNewEntity = (field: string, newValue: unknown) => {
    let userFunction: UserFunction | null | undefined = null;

    if (field === 'responsible' && currentEventId) {
      const value = newValue as User;

      if (value?.userEvents) {
        userFunction = findCurrentUserEvent(currentEventId)(value.userEvents)?.function;
      } else {
        userFunction = undefined;
      }
    }

    setNewEntity((entity) => {
      if (entity) {
        return {
          ...entity,
          [field]: newValue,
          ...(userFunction || userFunction === undefined ? { userFunction } : {}),
        };
      }

      return undefined;
    });
  };

  useEffect(() => {
    if (gridRef.current?.api) {
      if (
        !newEntity &&
        gridRef.current.api.getPinnedTopRowCount() > 0 &&
        gridRef.current.api.getFocusedCell()?.rowPinned === 'top'
      ) {
        gridRef.current.api.clearFocusedCell();
      }
      gridRef.current.api.setPinnedTopRowData(newEntity ? [newEntity] : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEntity]);

  return {
    isNewRowEntity,
    editNewEntity,
  };
}
